import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import Typography from '../Typography';
import FormLabel from '../FormLabel';
import CheckboxCheckedIcon from '../icons/CheckboxCheckedIcon';
import CheckboxUncheckedIcon from '../icons/CheckboxUncheckedIcon';
import RadioCheckedIcon from '../icons/RadioCheckedIcon';
import RadioUncheckedIcon from '../icons/RadioUncheckedIcon';

const icons = {
  checkbox: {
    checked: CheckboxCheckedIcon,
    unchecked: CheckboxUncheckedIcon,
  },
  radio: {
    checked: RadioCheckedIcon,
    unchecked: RadioUncheckedIcon,
  },
};

function isChecked(variant, option, value) {
  if (variant === 'radio') {
    return option === value;
  }
  return Array.isArray(value) && value.includes(option);
}

function getOnChange(variant, option, input) {
  if (variant === 'radio') {
    return () => input.onChange(option);
  }
  return event => {
    const value = Array.isArray(input.value) ? input.value : [];
    if (event.target.checked) {
      input.onChange([...value, option]);
    } else {
      input.onChange(value.filter(entry => entry !== option));
    }
  };
}

function OptionListInput(props) {
  const { meta, required, options, label, variant, input, classes, className } = props;
  const error = Boolean(meta && meta.touched && meta.error);
  return (
    <div className={classNames(className, classes.root, error && classes.error)}>
      <FormLabel>
        {label}
        {(required || error) && (
          <span className={classes.fieldLabelParticle}>
            {' ('}
            {meta.error || <FormattedMessage id="required" />}
            {')'}
          </span>
        )}
      </FormLabel>
      {options.map(option => {
        const checked = isChecked(variant, option.value, input.value);
        const Icon = icons[variant][checked ? 'checked' : 'unchecked'];
        return (
          <label key={option.value} className={classes.itemRoot}>
            <input
              type={variant}
              checked={checked}
              onChange={getOnChange(variant, option.value, input)}
              hidden
            />
            <Icon
              className={classNames(classes.icon, classes[variant], checked && classes.checked)}
            />
            <Typography className={classes.optionLabel} type="caption">
              {option.label}
            </Typography>
          </label>
        );
      })}
      <div className={classes.errorMarker} />
    </div>
  );
}

OptionListInput.propTypes = {
  options: PropTypes.array.isRequired,
  input: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  variant: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  required: PropTypes.bool,
  className: PropTypes.string,
  meta: PropTypes.object,
};

OptionListInput.defaultProps = {
  required: false,
  className: null,
  meta: null,
};

const stylesheet = theme => ({
  root: {
    position: 'relative',
  },
  error: {
    // empty class to be used by its children
  },
  fieldLabelParticle: {
    '$error &': {
      color: theme.palette.error[500],
    },
  },
  itemRoot: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing.unit,
    cursor: 'pointer',
    borderRadius: 4,
    transition: `background-color ${theme.transitions.duration.shortest}ms`,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.035)',
    },
  },
  optionLabel: {
    marginLeft: theme.spacing.unit,
  },
  icon: {
    transition: `color ${theme.transitions.duration.short}ms`,
    '$itemRoot:hover &': {
      color: theme.palette.success[500],
    },
  },
  checked: {
    color: theme.palette.success[500],
    '$itemRoot:hover $checkbox&': {
      color: theme.palette.success[300],
    },
  },
  checkbox: {
    // empty class to be used by "checked"
  },
  errorMarker: {
    backgroundColor: 'transparent',
    width: 2,
    position: 'absolute',
    bottom: theme.spacing.unit,
    height: `calc(100% - ${theme.spacing.medium + theme.spacing.unit}px)`,
    transition: `background-color ${theme.transitions.duration.complex}ms`,
    '$error &': {
      backgroundColor: theme.palette.error[500],
    },
  },
});

export default injectSheet(stylesheet)(OptionListInput);



// WEBPACK FOOTER //
// ./src/site-render/src/OptionListInput/OptionListInput.js